import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [isOpen, setIsOpen] = useState(false);

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('gorito_cart');
    if (savedCart) {
      try {
        setItems(JSON.parse(savedCart));
      } catch (e) {
        console.error('Failed to parse cart from localStorage');
      }
    }
  }, []);

  // Save cart to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('gorito_cart', JSON.stringify(items));
  }, [items]);

  const addItem = (item) => {
    setItems((prevItems) => {
      // Check if item with same artwork_id and size already exists
      const existingIndex = prevItems.findIndex(
        (i) => i.artwork_id === item.artwork_id && i.size === item.size
      );

      if (existingIndex >= 0) {
        // Update quantity
        const newItems = [...prevItems];
        newItems[existingIndex].quantity += item.quantity || 1;
        return newItems;
      }

      // Add new item
      return [...prevItems, { ...item, quantity: item.quantity || 1 }];
    });
    setIsOpen(true);
  };

  const removeItem = (artwork_id, size) => {
    setItems((prevItems) =>
      prevItems.filter((item) => !(item.artwork_id === artwork_id && item.size === size))
    );
  };

  const updateQuantity = (artwork_id, size, quantity) => {
    if (quantity <= 0) {
      removeItem(artwork_id, size);
      return;
    }

    setItems((prevItems) =>
      prevItems.map((item) =>
        item.artwork_id === artwork_id && item.size === size
          ? { ...item, quantity }
          : item
      )
    );
  };

  const clearCart = () => {
    setItems([]);
  };

  const getSubtotal = () => {
    return items.reduce((sum, item) => sum + item.unit_price * item.quantity, 0);
  };

  const getItemCount = () => {
    return items.reduce((sum, item) => sum + item.quantity, 0);
  };

  const openCart = () => setIsOpen(true);
  const closeCart = () => setIsOpen(false);
  const toggleCart = () => setIsOpen(!isOpen);

  // Check if cart contains only prints (can checkout online)
  const canCheckoutOnline = () => {
    return items.length > 0 && items.every((item) => item.artwork_type === 'print');
  };

  const value = {
    items,
    isOpen,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    getSubtotal,
    getItemCount,
    openCart,
    closeCart,
    toggleCart,
    canCheckoutOnline,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export default CartContext;
